import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useSnackbar } from "notistack";
import Box from "@mui/material/Box";
import { useQuery } from "@tanstack/react-query";
import { htmlDecode } from "../../utils/convertHtml";
import { s3baseUrl } from "../../config/config";
import ReactVideoPlayer from "src/components/ReactPlayers/ReactVideoPlayer";
import { admin_videos_detail } from "src/DAL/HelpingVideos/AdminHelpingVideos";
import { gr } from "src/design-system/tokens";
import icons from "src/design-system/icons";
import GrPage from "src/design-system/components/GrPage";
import GrPageHeader from "src/design-system/components/GrPageHeader";
import GrLoader from "src/design-system/components/GrLoader";
import GrRichText from "src/design-system/components/GrRichText";
import GrCoverImage from "src/design-system/components/GrCoverImage";
import { htmlText } from "../Programmes/programmeHelpers";

/**
 * One helping video (/helping_videos_category/:id/helping-videos/:slug): the video (or its image)
 * on top of one card, the description under it. Locked videos show the locked info instead.
 */

// The video (or its image) stands on its own, as on video sites; the description is plain text under it
// (four lines, then "View more").
const mediaSx = {
  overflow: "hidden",
  borderRadius: gr.radius.card,
  boxShadow: gr.shadow.card,
  background: gr.color.tint, // behind the player while it loads, and behind its "not available" note
  mb: gr.space[6],
};

export default function HelpingVideoDetail() {
  const params = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const location = useLocation();
  const { data: programmesDetail = {}, isLoading } = useQuery({
    queryKey: ["helpingVideoDetail", params.slug],
    queryFn: async () => {
      const result = await admin_videos_detail(params.slug);
      if (result?.code !== 200) {
        throw new Error(result?.message);
      }
      return result;
    },
    select: (result) => result?.helping_video || {},
    onError: (error) => {
      enqueueSnackbar(error?.message, { variant: "error" });
    },
  });
  const get_programme_image = () => {
    if (programmesDetail?.program_lock_status === true) {
      return programmesDetail?.locked_program_info.image;
    } else {
      return programmesDetail?.thumbnail_image?.thumbnail_1;
    }
  };
  const get_programme_video = () => {
    if (programmesDetail?.program_lock_status === true) {
      return programmesDetail?.locked_program_info.video_url;
    } else {
      return programmesDetail?.video_url;
    }
  };

  const get_detailed_description = () => {
    if (programmesDetail?.program_lock_status === true) {
      return programmesDetail?.locked_program_info.detailed_description;
    } else {
      return programmesDetail?.long_description;
    }
  };

  // The title from the list shows at once; opened by link, it comes with the video (not "undefined"
  // while loading).
  const title = location.state
    ? htmlDecode(location.state.title)
    : isLoading
    ? ""
    : htmlDecode(programmesDetail?.title);

  const renderContent = () => {
    const video = get_programme_video();
    const image = get_programme_image();
    const description = get_detailed_description();
    const hasDescription = Boolean(htmlText(description));
    return (
      <>
        {video || image ? (
          <Box sx={mediaSx}>
            {video ? (
              <ReactVideoPlayer url={video} />
            ) : (
              <GrCoverImage
                src={s3baseUrl + image}
                alt={title}
                placeholderIcon={icons.video}
              />
            )}
          </Box>
        ) : null}
        {hasDescription ? (
          <GrRichText html={description} collapsedLines={4} />
        ) : null}
      </>
    );
  };

  return (
    <GrPage>
      <GrPageHeader onBack={() => navigate(-1)} title={title} />

      {isLoading ? <GrLoader /> : renderContent()}
    </GrPage>
  );
}
